import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './AutoplayVideo.css';

// Vertical (9:16) video that autoplays muted, loops, and has a mute / unmute button on top.
// The button shows its full label for LABEL_SECONDS after the page loads, then collapses to a small
// icon-only button that stays tappable. After a tap it shows the new label for TAP_SECONDS, then collapses again.
// Props: src - video URL (e.g. `${process.env.PUBLIC_URL}/videos/message-from-ht.mp4`); empty shows a placeholder.
// Labels come from the 'unmute' and 'mute' keys in the messageFrom namespace.
const LABEL_SECONDS = 5;
const TAP_SECONDS = 2;

function AutoplayVideo({ src }) {
  const { t } = useTranslation('messageFrom');
  const videoRef = useRef(null);
  const timerRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [expanded, setExpanded] = useState(true);

  // Show the full label now, and collapse it again after `seconds` (a new call restarts the timer)
  const showLabelFor = (seconds) => {
    setExpanded(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setExpanded(false), seconds * 1000);
  };

  useEffect(() => {
    showLabelFor(LABEL_SECONDS);
    return () => clearTimeout(timerRef.current);
  }, []);

  // Flip the sound on/off; the button label follows the state
  const toggleMute = () => {
    videoRef.current.muted = !muted;
    setMuted(!muted);
    showLabelFor(TAP_SECONDS);
  };

  const label = muted ? t('unmute') : t('mute');

  return (
    <div className="autoplayVideo">
      {src ? (
        <>
          {/* Browsers only allow autoplay when the video starts muted */}
          {/* Tapping the video itself also toggles the sound (easier target than the small button) */}
          <video ref={videoRef} src={src} autoPlay muted loop playsInline onClick={toggleMute} />
          <button
            type="button"
            className={`autoplayVideoBtn ${expanded ? '' : 'autoplayVideoBtnSmall'}`}
            onClick={(event) => {
              event.stopPropagation();
              toggleMute();
            }}
            aria-label={label}
          >
            <span className="autoplayVideoIcon" aria-hidden="true">{muted ? '🔇' : '🔊'}</span>
            <span className="autoplayVideoLabel">{label}</span>
          </button>
        </>
      ) : (
        <div className="autoplayVideoPlaceholder">▶</div>
      )}
    </div>
  );
}

export default AutoplayVideo;

